/**
 * ClientOverview — the workspace member's Home surface. Renders the shared
 * DashboardHome (Pipeline + Profit & Loss + Coming up) scoped to the member's
 * own projects, so a member sees the same layout as the CG ops QSDashboard but
 * only their own data. Scoping via getProjectsByClient(company.id) matters: the
 * in-memory dataset can hold more than one client's projects, so we must not
 * hand DashboardHome the unscoped workspace lists.
 */

import { useMemo } from 'react';
import { useData } from '../../../contexts/DataContext';
import type { User, Company } from '../../../types/auth';
import { DashboardHome } from '../../dashboard/DashboardHome';

interface ClientOverviewProps {
  user?: User;
  company?: Company;
  // Retained for call-site compatibility (ClientDashboard passes it); the QS
  // layout has no projects table, so there is no row to open from here.
  onProjectSelect?: (projectId: string, projectName: string) => void;
}

export function ClientOverview({ company, user }: ClientOverviewProps) {
  const {
    getProjectsByClient,
    getContractItemsByProject,
    getInterimApplicationsByProject,
    getTasksByProject,
  } = useData();

  const clientProjects = useMemo(
    () => (company?.id ? getProjectsByClient(company.id) : []),
    [getProjectsByClient, company?.id],
  );

  const contractItems = useMemo(
    () => clientProjects.flatMap((p) => getContractItemsByProject(p.id)),
    [clientProjects, getContractItemsByProject],
  );

  const interimApplications = useMemo(
    () => clientProjects.flatMap((p) => getInterimApplicationsByProject(p.id)),
    [clientProjects, getInterimApplicationsByProject],
  );

  const tasks = useMemo(
    () => clientProjects.flatMap((p) => getTasksByProject(p.id)),
    [clientProjects, getTasksByProject],
  );

  return (
    <DashboardHome
      salesWorkspaceId={user?.activeWorkspaceId ?? user?.workspaceId ?? user?.workspace_id}
      projects={clientProjects}
      tasks={tasks}
      contractItems={contractItems}
      interimApplications={interimApplications}
    />
  );
}
